import React, {useContext, useEffect, useState, useCallback} from "react";
import StyleContext from "../../contexts/StyleContext";
import {useAuth} from "../../contexts/AuthContext";
import {
  adminGetResume,
  adminUpdateResume,
  adminGetResumeAnalytics
} from "../../services/api";
import GlobalLoader from "../../components/loader/GlobalLoader";
import "../../styles/_form.scss";
import "./ResumeEditor.scss";
import "../../styles/_admin-overrides.scss";

const EMPTY = {
  header:   {name: "", title: "", location: "", email: "", phone: "", linkedin: "", linkedinLabel: ""},
  summary:  "",
  experience:     [],
  projects:       [],
  education:      [],
  skills:         [],
  certifications: [],
};

const addItem = (arr, item) => [...(arr || []), item];
const setItem = (arr, idx, patch) => arr.map((v, i) => i === idx ? {...v, ...patch} : v);
const delItem = (arr, idx) => arr.filter((_, i) => i !== idx);

// Small reusable field for the editor — keeps label above the input
// with intentional spacing and avoids the historical <label>wraps<input> bug.
const Field = ({label, children, hint}) => (
  <div className="rd-form-group">
    <label className="rd-form-group__label">{label}</label>
    {children}
    {hint && <p className="rd-form-group__hint">{hint}</p>}
  </div>
);

const ResumeEditor = () => {
  const {isDark} = useContext(StyleContext);
  const {token}  = useAuth();
  const [data, setData]     = useState(EMPTY);
  const [analytics, setAna] = useState(null);
  const [loading, setLoad]  = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [savedAt, setSaved] = useState(null);

  const load = useCallback(async () => {
    setLoad(true); setError("");
    try {
      const [r, a] = await Promise.all([
        adminGetResume(token),
        adminGetResumeAnalytics(token).catch(() => null),
      ]);
      const incoming = r?.resume && Object.keys(r.resume).length ? r.resume : EMPTY;
      setData({...EMPTY, ...incoming, header: {...EMPTY.header, ...(incoming.header || {})}});
      if (a?.totals) setAna(a);
    } catch (e) {
      setError(e.message || "Could not load resume");
    } finally { setLoad(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      await adminUpdateResume(data, token);
      setSaved(new Date());
    } catch (e) {
      setError(e.message || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <GlobalLoader inline label="Loading resume" />;

  const patchHeader = patch => setData(d => ({...d, header: {...d.header, ...patch}}));
  const formClass = `rd-form rd-redit__form${isDark ? " rd-form--dark" : ""}`;

  return (
    <div className={`rd-redit ${isDark ? "dark" : ""}`}>
      {/* ---- Analytics (admin-only) ---- */}
      {analytics && (
        <div className="rd-redit__metrics">
          <div>
            <span>Views</span>
            <strong>{analytics.totals?.views ?? 0}</strong>
          </div>
          <div>
            <span>PDF downloads</span>
            <strong>{analytics.totals?.downloads ?? 0}</strong>
          </div>
          <div>
            <span>Last viewed</span>
            <strong>{analytics.totals?.last_view || "—"}</strong>
          </div>
          <div>
            <span>Last download</span>
            <strong>{analytics.totals?.last_dl || "—"}</strong>
          </div>
        </div>
      )}

      <div className="rd-redit__header">
        <div>
          <h2>Resume editor</h2>
          <p className="admin__lead" style={{margin: 0}}>
            Edit the live CV. Saved changes appear on <code>/resume</code> immediately.
          </p>
        </div>
        <div className="rd-redit__save">
          {savedAt && <span>Saved · {savedAt.toLocaleTimeString()}</span>}
          <button onClick={save} disabled={saving} className="rd-form__submit">
            {saving ? (
              <>
                <GlobalLoader inline compact />
                <span>Saving…</span>
              </>
            ) : "Save changes"}
          </button>
        </div>
      </div>

      {error && <p className="rd-form__error">{error}</p>}

      <div className={formClass}>
        {/* ---- Header ---- */}
        <section className="rd-redit__section">
          <h3>Header</h3>
          <div className="rd-redit__grid">
            <Field label="Name">
              <input value={data.header.name} onChange={e => patchHeader({name: e.target.value})} />
            </Field>
            <Field label="Title / Role">
              <input value={data.header.title} onChange={e => patchHeader({title: e.target.value})} />
            </Field>
            <Field label="Location">
              <input value={data.header.location} onChange={e => patchHeader({location: e.target.value})} />
            </Field>
            <Field label="Email">
              <input type="email" value={data.header.email} onChange={e => patchHeader({email: e.target.value})} />
            </Field>
            <Field label="Phone">
              <input type="tel" value={data.header.phone} onChange={e => patchHeader({phone: e.target.value})} />
            </Field>
            <Field label="LinkedIn URL">
              <input value={data.header.linkedin} onChange={e => patchHeader({linkedin: e.target.value})} />
            </Field>
            <Field label="LinkedIn label" span={2} hint="Short display text — e.g. /in/rashidahmad03">
              <input value={data.header.linkedinLabel} onChange={e => patchHeader({linkedinLabel: e.target.value})} />
            </Field>
          </div>
        </section>

        {/* ---- Summary ---- */}
        <section className="rd-redit__section">
          <h3>Professional summary</h3>
          <Field label="Summary">
            <textarea
              rows={5}
              value={data.summary}
              onChange={e => setData(d => ({...d, summary: e.target.value}))}
            />
          </Field>
        </section>

        {/* ---- Experience ---- */}
        <section className="rd-redit__section">
          <div className="rd-redit__section-head">
            <h3>Work experience</h3>
            <button
              type="button"
              className="rd-redit__add"
              onClick={() => setData(d => ({
                ...d,
                experience: addItem(d.experience, {company: "", role: "", from: "", to: "", location: ""})
              }))}
            >
              + Add
            </button>
          </div>

          {data.experience.map((x, i) => (
            <div key={i} className="rd-redit__card">
              <div className="rd-redit__grid">
                <Field label="Company">
                  <input value={x.company} onChange={e => setData(d => ({...d, experience: setItem(d.experience, i, {company: e.target.value})}))} />
                </Field>
                <Field label="Role">
                  <input value={x.role} onChange={e => setData(d => ({...d, experience: setItem(d.experience, i, {role: e.target.value})}))} />
                </Field>
                <Field label="From">
                  <input value={x.from} onChange={e => setData(d => ({...d, experience: setItem(d.experience, i, {from: e.target.value})}))} />
                </Field>
                <Field label="To">
                  <input value={x.to} onChange={e => setData(d => ({...d, experience: setItem(d.experience, i, {to: e.target.value})}))} />
                </Field>
                <Field label="Location" span={2}>
                  <input value={x.location} onChange={e => setData(d => ({...d, experience: setItem(d.experience, i, {location: e.target.value})}))} />
                </Field>
              </div>
              <button
                type="button"
                className="rd-redit__remove"
                onClick={() => setData(d => ({...d, experience: delItem(d.experience, i)}))}
              >
                Remove entry
              </button>
            </div>
          ))}
        </section>

        {/* ---- Projects ---- */}
        <section className="rd-redit__section">
          <div className="rd-redit__section-head">
            <h3>Key projects</h3>
            <button
              type="button"
              className="rd-redit__add"
              onClick={() => setData(d => ({
                ...d,
                projects: addItem(d.projects, {name: "", period: "", bullets: [""]})
              }))}
            >
              + Add
            </button>
          </div>

          {data.projects.map((p, i) => (
            <div key={i} className="rd-redit__card">
              <div className="rd-redit__grid">
                <Field label="Project name">
                  <input value={p.name} onChange={e => setData(d => ({...d, projects: setItem(d.projects, i, {name: e.target.value})}))} />
                </Field>
                <Field label="Period">
                  <input value={p.period} onChange={e => setData(d => ({...d, projects: setItem(d.projects, i, {period: e.target.value})}))} />
                </Field>
                <Field label="Highlights" span={2} hint="One bullet per line.">
                  <textarea
                    rows={4}
                    value={(p.bullets || []).join("\n")}
                    onChange={e => setData(d => ({
                      ...d,
                      projects: setItem(d.projects, i, {bullets: e.target.value.split("\n")})
                    }))}
                  />
                </Field>
              </div>
              <button
                type="button"
                className="rd-redit__remove"
                onClick={() => setData(d => ({...d, projects: delItem(d.projects, i)}))}
              >
                Remove project
              </button>
            </div>
          ))}
        </section>

        {/* ---- Education ---- */}
        <section className="rd-redit__section">
          <div className="rd-redit__section-head">
            <h3>Education</h3>
            <button
              type="button"
              className="rd-redit__add"
              onClick={() => setData(d => ({
                ...d,
                education: addItem(d.education, {school: "", degree: "", from: "", to: ""})
              }))}
            >
              + Add
            </button>
          </div>

          {data.education.map((e, i) => (
            <div key={i} className="rd-redit__card">
              <div className="rd-redit__grid">
                <Field label="School">
                  <input value={e.school} onChange={ev => setData(d => ({...d, education: setItem(d.education, i, {school: ev.target.value})}))} />
                </Field>
                <Field label="Degree">
                  <input value={e.degree} onChange={ev => setData(d => ({...d, education: setItem(d.education, i, {degree: ev.target.value})}))} />
                </Field>
                <Field label="From">
                  <input value={e.from} onChange={ev => setData(d => ({...d, education: setItem(d.education, i, {from: ev.target.value})}))} />
                </Field>
                <Field label="To">
                  <input value={e.to} onChange={ev => setData(d => ({...d, education: setItem(d.education, i, {to: ev.target.value})}))} />
                </Field>
              </div>
              <button
                type="button"
                className="rd-redit__remove"
                onClick={() => setData(d => ({...d, education: delItem(d.education, i)}))}
              >
                Remove entry
              </button>
            </div>
          ))}
        </section>

        {/* ---- Skills ---- */}
        <section className="rd-redit__section">
          <h3>Skills</h3>
          <Field label="Skills" hint="Comma-separated values.">
            <textarea
              rows={3}
              value={(data.skills || []).join(", ")}
              onChange={e => setData(d => ({
                ...d,
                skills: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
              }))}
            />
          </Field>
        </section>

        {/* ---- Certifications ---- */}
        <section className="rd-redit__section">
          <h3>Certifications</h3>
          <Field label="Certifications" hint="One per line.">
            <textarea
              rows={5}
              value={(data.certifications || []).join("\n")}
              onChange={e => setData(d => ({
                ...d,
                certifications: e.target.value.split("\n").map(s => s.trim()).filter(Boolean)
              }))}
            />
          </Field>
        </section>
      </div>

      <div className="rd-redit__footer">
        <button onClick={save} disabled={saving} className="rd-form__submit">
          {saving ? (
            <>
              <GlobalLoader inline compact />
              <span>Saving…</span>
            </>
          ) : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default ResumeEditor;
